import { createFileRoute } from "@tanstack/react-router";
import { localProductionSection } from "@/lib/seo/local-production";
import { LandingPage, type LandingFAQ } from "@/components/site/landing/LandingPage";
import { breadcrumbs, faqPage, localBusinessForCity, speakable, aeoGeo } from "@/lib/seo/schema";

const FAQ: LandingFAQ[] = [
  {
    q: "Vad kostar animation i Malmö?",
    a: "Motion graphics 15–30 sekunder kostar 25 000–60 000 kr. Animerad förklaringsfilm 60–90 sekunder 60 000–150 000 kr. 3D-animation från 120 000 kr. Löpande produktion via abonnemang från 14 995 kr/mån.",
  },
  {
    q: "Hur lång är leveranstiden?",
    a: "Motion graphics 2–3 veckor, förklaringsfilm 3–5 veckor och 3D-animation 5–8 veckor från godkänt manus och storyboard.",
  },
  {
    q: "Kan ni producera på både svenska och engelska?",
    a: "Ja. Vi levererar speaker och undertexter på svenska och engelska i samma produktion — vanligt hos bolag i Öresundsregionen som kommunicerar mot Danmark och övriga Europa.",
  },
  {
    q: "Vad behöver ni från oss för att komma igång?",
    a: "Ett mål, en målgrupp och det underlag ni har: produktblad, skisser, CAD-filer eller befintlig grafisk profil. Vi tar fram manus och storyboard därifrån.",
  },
  {
    q: "Kan animationen återanvändas i flera kanaler?",
    a: "Ja. Vi bygger scenerna modulärt så att samma animation kan klippas till kortformat för sociala medier, loopas på mässa och användas i sälj- och onboardingmaterial.",
  },
];

export const Route = createFileRoute("/animation_/malmo")({
  head: () => ({
    meta: [
      { title: "Animation och förklaringsfilm i Malmö | Fenny" },
      {
        name: "description",
        content:
          "Animation i Malmö och Öresundsregionen: motion graphics, förklaringsfilm och 3D. Pris 25 000–150 000 kr, leverans 2–5 veckor.",
      },
      { property: "og:title", content: "Animation och förklaringsfilm i Malmö | Fenny" },
      {
        property: "og:description",
        content: "Animerad film för bolag i Malmö och Öresundsregionen — svenska och engelska versioner i samma produktion.",
      },
      { property: "og:type", content: "website" },
      { property: "og:url", content: "https://fenny.se/animation/malmo" },
    ],
    links: [{ rel: "canonical", href: "https://fenny.se/animation/malmo" }],
    scripts: [
      { type: "application/ld+json", children: JSON.stringify(aeoGeo({path: "/animation/malmo", name: "Animation Malmö — motion graphics och förklaringsfilm | Fenny", description: "Animation i Malmö och Öresundsregionen: motion graphics, förklaringsfilm och 3D. Pris 25 000–150 000 kr, leverans 2–5 veckor."})) },
      { type: "application/ld+json", children: JSON.stringify(speakable([".lede"])) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          localBusinessForCity({
            city: "Malmö",
            path: "/animation/malmo",
            description:
              "Animation och motion graphics i Malmö — förklaringsfilm, 3D och animerad reklamfilm.",
            postalRegion: "Skåne län",
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(faqPage(FAQ)) },
      {
        type: "application/ld+json",
        children: JSON.stringify(
          breadcrumbs([
            { name: "Hem", path: "/" },
            { name: "Animation", path: "/motion-graphics" },
            { name: "Malmö", path: "/animation/malmo" },
          ]),
        ),
      },
    ],
  }),
  component: AnimationMalmoPage,
});

function AnimationMalmoPage() {
  return (
    <LandingPage
      eyebrow="Animation Malmö"
      title={<>Animation i Malmö — begripligt på 60 sekunder.</>}
      lede="Motion graphics, animerad förklaringsfilm och 3D för bolag i Malmö och Öresundsregionen. Svenska och engelska versioner i samma produktion, fast pris och storyboard innan produktionen startar."
      tldr={
        <p>
          Animation i Malmö kostar typiskt <strong>25 000–150 000 kr</strong> och
          levereras på <strong>2–5 veckor</strong>. Fenny producerar motion
          graphics, förklaringsfilm och 3D-animation — med tvåspråkig leverans
          för bolag som kommunicerar mot både Sverige och Danmark.
        </p>
      }
      ctaPrimary={{ label: "Boka strategimöte", to: "/kontakt" }}
      ctaSecondary={{ label: "Se priser", to: "/priser" }}
      trackingId="animation-malmo"
      bullets={[
        { title: "Tvåspråkig leverans", body: "Svensk och engelsk speaker plus undertexter i samma produktion." },
        { title: "Modulära scener", body: "Samma animation klipps om till kortformat, mässloop och säljmaterial." },
        { title: "Fast pris", body: "Offert med fast pris och tidsplan innan produktionen startar." },
      ]}
      pricing={{
        heading: "Vad kostar animation i Malmö?",
        intro: "Riktpriser exklusive moms.",
        rows: [
          { format: "Motion graphics 15–30 sek", scope: "Manus, design, animation, ljud", price: "25 000–60 000 kr", lead: "2–3 veckor" },
          { format: "Förklaringsfilm 60–90 sek", scope: "Storyboard, illustration, speaker", price: "60 000–150 000 kr", lead: "3–5 veckor" },
          { format: "3D-animation", scope: "Modellering, rendering, komposition", price: "från 120 000 kr", lead: "5–8 veckor" },
          { format: "Löpande animation", scope: "Abonnemang med kontinuerlig output", price: "från 14 995 kr/mån", lead: "10–14 dagar till första klipp" },
        ],
      }}
      sections={[
        localProductionSection("malmo", "Malmö"),
        {
          id: "nar-animation",
          heading: "När animation är rätt val",
          body: (
            <>
              <p>
                När produkten är digital, processen abstrakt eller när det som ska
                visas finns inuti en maskin, under mark eller i framtiden. Då blir
                animation både billigare och tydligare än en filmad produktion.
              </p>
              <p>
                Behöver ni filmat material istället, se{" "}
                <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/filmproduktion/malmo">
                  filmproduktion i Malmö
                </a>
                .
              </p>
            </>
          ),
        },
        {
          id: "tekniker",
          heading: "Tekniker vi arbetar i",
          body: (
            <p>
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/motion-graphics">Motion graphics</a>,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/animerad-forklaringsfilm">animerad förklaringsfilm</a>,{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/animerad-reklamfilm">animerad reklamfilm</a> och{" "}
              <a className="underline decoration-mint/40 underline-offset-4 hover:text-mint" href="/3d-animation">3D-animation</a>.
            </p>
          ),
        },
      ]}
      faq={FAQ}
      caseLimit={4}
      riskReducers
      closing={{
        title: "Ska ni göra animation i Malmö?",
        body: "Boka 30 minuter så tittar vi på format, längd och prisnivå för ert case.",
        primary: { label: "Boka strategimöte", to: "/kontakt" },
        secondary: { label: "Se case", to: "/case" },
      }}
    />
  );
}
